// ABOUTME: React.lazy wrapper that reloads the page once on a stale-chunk import failure
// ABOUTME: Lets tabs opened before a deploy recover by re-fetching the current chunk manifest

import { lazy } from 'react';

const RELOAD_TS_KEY = 'lazyChunkReloadTs';
const RELOAD_COOLDOWN_MS = 10_000;

// Cross-browser messages for a failed dynamic import (chunk fetch), as opposed to
// a module that threw at evaluation time — only the former is worth reloading for.
export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /dynamically imported module|importing a module script failed|failed to fetch|chunkloaderror/i.test(
    message
  );
}

// A reload is warranted only for a chunk-load error, and only if we haven't just
// reloaded — the cooldown prevents an infinite loop when a chunk is genuinely gone.
export function shouldReloadOnChunkError(err: unknown, lastReloadTs: number, now: number): boolean {
  return isChunkLoadError(err) && now - lastReloadTs > RELOAD_COOLDOWN_MS;
}

export function lazyWithReload(factory: Parameters<typeof lazy>[0]) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      let lastReloadTs = 0;
      try {
        lastReloadTs = Number(sessionStorage.getItem(RELOAD_TS_KEY) ?? '0');
      } catch {
        // sessionStorage unavailable (private mode / SSR) — fall through and rethrow.
      }
      if (shouldReloadOnChunkError(err, lastReloadTs, Date.now())) {
        try {
          sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
        } catch {
          // best-effort; the cooldown just won't persist across the reload.
        }
        window.location.reload();
        return new Promise<Awaited<ReturnType<typeof factory>>>(() => {
          // Never resolves — the reload replaces this document before React renders.
        });
      }
      throw err;
    }
  });
}
